import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

/**
 * Bausteine für einreihige Seitenköpfe (Ticket „obere Leiste einreihig",
 * Paket C): TabNav + tabPillClass teilen den Pill-Stil zwischen Routen-Tabs
 * (Link) und State-Tabs (button) — vorher byte-identisch dupliziert in
 * personnel-/settings-layout und hand-gerollt in time-accounts,
 * import-export und stammdaten.
 */

export function TabNav({
  ariaLabel,
  className,
  children,
}: {
  ariaLabel: string
  className?: string
  children: ReactNode
}) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1 overflow-x-auto sm:flex-wrap min-w-0', className)}
    >
      {children}
    </nav>
  )
}

/** Pill-Klassen für ein einzelnes Tab-Element — für Link UND button nutzbar. */
export function tabPillClass(active: boolean, className?: string): string {
  return cn(
    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 touch:min-h-11 text-xs sm:text-sm transition-colors whitespace-nowrap',
    active
      ? 'bg-accent/15 text-accent font-medium'
      : 'text-text-secondary hover:text-text-primary hover:bg-bg-overlay/40',
    className,
  )
}

/**
 * Kompakter Ansichts-Umschalter (Woche/Monat, Tag/…/Jahr) — vorher dreimal
 * lokal definiert (calendar, team-planning, disposition).
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T
  onChange: (v: T) => void
  options: readonly { id: T; label: string }[]
  className?: string
}) {
  return (
    <div
      className={cn(
        'inline-flex rounded-md border border-border-default bg-bg-overlay/40 p-0.5',
        className,
      )}
    >
      {options.map((o) => {
        const active = value === o.id
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.id)}
            // flex-1 + px-2 mobil: Optionen teilen sich die Zeile gleichmäßig
            // (390px-Fall Kalender); sm: stellt die kompakte Desktop-Optik her.
            className={cn(
              'flex-1 sm:flex-initial rounded px-2 sm:px-3 py-1.5 touch:py-3 text-xs transition-colors',
              active ? 'bg-accent/15 text-accent' : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Badge-Text für Zähler in Tabs: null bei fehlendem, ungültigem (NaN) oder
 * nicht-positivem Wert, „99+" ab 100.
 */
export function tabBadgeLabel(count: number | null | undefined): string | null {
  if (typeof count !== 'number' || Number.isNaN(count) || count <= 0) return null
  return count >= 100 ? '99+' : String(count)
}

export function TabBadge({ count }: { count?: number | null }) {
  const label = tabBadgeLabel(count)
  if (label === null) return null
  return (
    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-2xs font-semibold leading-none text-bg-base tabular-nums">
      {label}
    </span>
  )
}
